import { useEffect, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authedFetch } from '../lib/supabase';
import { useAuth } from '../stores/auth';
import {
  Bell, Shield, Database, Users, AlertTriangle, Calendar, MessageSquare,
  FileWarning, CheckCircle2, X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CATEGORY_ICONS = {
  clinical: Calendar,
  compliance: Shield,
  operations: Users,
  security: FileWarning,
  general: Bell,
};
const SEVERITY_COLOR = {
  critical: '#7A2F22',
  high: '#8A3B2B',
  medium: '#0F2A4A',
  low: '#0B6F6F',
  info: '#0B6F6F',
};

export function NotificationBell() {
  const { profile } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const q = useQuery({
    queryKey: ['notifications'],
    queryFn: () => authedFetch('/notifications'),
    enabled: !!profile,
    // Data lives in Neon (no Supabase Realtime) — lightweight 20s polling keeps the bell fresh.
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
  });

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const notifications = q.data?.notifications || [];
  const unread = q.data?.unread_count || 0;

  const markRead = async (id) => {
    await authedFetch(`/notifications/${id}/read`, { method: 'POST' });
    qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  const markAllRead = async () => {
    await authedFetch('/notifications/mark-all-read', { method: 'POST' });
    qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  const onClick = (n) => {
    if (!n.is_read && !String(n.id).startsWith('live::')) markRead(n.id);
    setOpen(false);
    if (n.deep_link) nav(n.deep_link);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="notification-bell-wrap">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen(o => !o)}
        className="relative h-11 w-11 rounded-full hover:bg-[var(--color-surface-2)] flex items-center justify-center transition-colors"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Bell size={18} className="text-[var(--color-slate)]" aria-hidden="true" />
        {unread > 0 && (
          <span
            data-testid="notification-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#8A3B2B] text-white text-[10px] font-semibold flex items-center justify-center"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-[var(--color-border)] z-50 overflow-hidden" data-testid="notification-drawer">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
            <div>
              <h3 className="font-display font-semibold text-[var(--color-ink)] text-sm">Notifications</h3>
              <p className="text-[10px] text-[var(--color-muted)]">{unread} unread · {notifications.length} total</p>
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button data-testid="notification-mark-all" onClick={markAllRead} className="btn btn-ghost text-xs">
                  <CheckCircle2 size={12} /> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} aria-label="Close notifications" className="btn btn-ghost text-xs"><X size={14} aria-hidden="true" /></button>
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {q.isLoading ? (
              <p className="text-xs text-center py-6 text-[var(--color-muted)]">Loading…</p>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center" data-testid="notification-empty">
                <CheckCircle2 size={28} className="mx-auto text-[#0B6F6F] mb-2" />
                <p className="text-sm text-[var(--color-slate)]">Inbox zero.</p>
                <p className="text-[11px] text-[var(--color-muted)] mt-0.5">Nothing pending right now.</p>
              </div>
            ) : (
              notifications.map(n => {
                const Icon = CATEGORY_ICONS[n.category] || Bell;
                const fg = SEVERITY_COLOR[n.severity] || '#0F2A4A';
                return (
                  <button
                    key={n.id}
                    onClick={() => onClick(n)}
                    className={`w-full text-left px-4 py-3 border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-surface-2)] transition-colors ${n.is_read ? 'opacity-70' : ''}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: `${fg}1A`, color: fg }}>
                        <Icon size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm text-[var(--color-ink)] truncate">{n.title}</span>
                          {!n.is_read && <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-teal)] shrink-0" />}
                        </div>
                        {n.body && <p className="text-xs text-[var(--color-muted)] mt-0.5 line-clamp-2">{n.body}</p>}
                        <p className="text-[10px] text-[var(--color-muted)] mt-1">
                          {timeAgo(n.created_at)} · <span className="capitalize">{n.category}</span>
                          {n.severity && n.severity !== 'info' && (
                            <span className="ml-1 uppercase tracking-wider" style={{ color: fg }}>· {n.severity}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
}
